import { useEffect } from "react";

/**
 * Subtle mouse-follow parallax. Attach `containerRef` to the outer section
 * you want to watch mouse position within, and `targetRef` to the element
 * that should drift. `strength` scales the max translation in pixels
 * (default 14 = very subtle Vercel/Linear-style drift).
 */
export default function useParallax(containerRef, targetRef, strength = 14) {
  useEffect(() => {
    const c = containerRef?.current;
    const t = targetRef?.current;
    if (!c || !t) return;
    let raf = 0;
    let px = 0, py = 0;
    const onMove = (e) => {
      const rect = c.getBoundingClientRect();
      const relX = (e.clientX - rect.left) / rect.width  - 0.5;   // -0.5..0.5
      const relY = (e.clientY - rect.top)  / rect.height - 0.5;
      px = relX * strength;
      py = relY * strength;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      px = 0; py = 0;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const apply = () => {
      raf = 0;
      t.style.transform = `translate3d(${px.toFixed(2)}px, ${py.toFixed(2)}px, 0)`;
    };
    t.style.transition = "transform 480ms cubic-bezier(.16,.84,.2,1)";
    c.addEventListener("mousemove", onMove);
    c.addEventListener("mouseleave", onLeave);
    return () => {
      c.removeEventListener("mousemove", onMove);
      c.removeEventListener("mouseleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
      t.style.transform = "";
      t.style.transition = "";
    };
  }, [containerRef, targetRef, strength]);
}
